/**
 * Utility functions for date formatting across the TypoGuard application
 * 
 * This module provides standardized date formatting for consistent display
 * throughout the application, with support for localization and different
 * formatting needs (with/without time).
 */

/**
 * Format a date for user-friendly display in the UI
 * 
 * @param {Date|string} date - Date object or ISO string to format
 * @param {boolean} withTime - Whether to include time in the formatted output
 * @param {string} locale - Locale to use for formatting (defaults to browser locale)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, withTime = false, locale = navigator.language) => {
  if (!date) return '';
  
  // Convert string to Date object if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Handle invalid dates
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to formatDate:', date);
    return '';
  }
  
  try {
    if (withTime) {
      // Format with time: "3 July 2025 at 14:25"
      return dateObj.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(',', ' at');
    } else {
      // Format without time: "3 July 2025"
      return dateObj.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    // Fallback to ISO format if there's an error with localization
    return withTime ? dateObj.toISOString().split('T')[0] : dateObj.toISOString();
  }
};

/**
 * Format a date as a relative time (e.g., "2 days ago")
 * Useful for activity feeds and timelines
 * 
 * @param {Date|string} date - Date object or ISO string to format
 * @param {string} locale - Locale to use for formatting (defaults to browser locale)
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date, locale = navigator.language) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to formatRelativeTime:', date);
    return '';
  }
  
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const now = new Date();
    const diffInSeconds = Math.floor((dateObj - now) / 1000);
    
    // Convert to appropriate unit
    if (Math.abs(diffInSeconds) < 60) {
      return rtf.format(Math.floor(diffInSeconds), 'second');
    } else if (Math.abs(diffInSeconds) < 3600) {
      return rtf.format(Math.floor(diffInSeconds / 60), 'minute');
    } else if (Math.abs(diffInSeconds) < 86400) {
      return rtf.format(Math.floor(diffInSeconds / 3600), 'hour');
    } else if (Math.abs(diffInSeconds) < 2592000) {
      return rtf.format(Math.floor(diffInSeconds / 86400), 'day');
    } else if (Math.abs(diffInSeconds) < 31536000) {
      return rtf.format(Math.floor(diffInSeconds / 2592000), 'month');
    } else {
      return rtf.format(Math.floor(diffInSeconds / 31536000), 'year');
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    // Fallback to standard date if RelativeTimeFormat is not supported
    return formatDate(dateObj);
  }
};

/**
 * Get ISO 8601 formatted date string (for API/data export)
 * 
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} ISO formatted date string
 */
export const getISODateString = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to getISODateString:', date);
    return '';
  }
  
  return dateObj.toISOString();
};
